/**
 * Google Ads campaign-creation API client (P1).
 *
 * Backend routes (api/routes/google_ads_campaigns.py):
 *   POST /google-ads/campaigns/setup
 *   GET  /google-ads/campaigns/runs?tenant_id=...
 *   GET  /google-ads/campaigns/runs/{run_id}
 *   GET  /google-ads/campaigns/runs/{run_id}/clusters
 *   POST /google-ads/campaigns/runs/{run_id}/approve
 *   POST /google-ads/campaigns/runs/{run_id}/reject
 *
 * Master flag: GOOGLE_ADS_ENABLED + GOOGLE_ADS_CAMPAIGN_CREATION_ENABLED
 */
import { apiClient } from './apiClient';

const CAMPAIGN_SETUP_TIMEOUT_MS = 180_000;
const CONVERSION_TRACKING_GATE_MESSAGE =
  'Campaign creation is blocked because this lead-gen/ROI goal needs a qualified primary Google Ads conversion action. Configure a lead, call, or booked appointment conversion as primary, or switch the campaign goal to Traffic / clicks for testing.';
const CONVERSION_TRACKING_GATE_PATTERN =
  /CONVERSION_TRACKING_NOT_ENABLED|Conversion tracking is not enabled|Lead-gen conversion tracking|conversion_tracking_gate|qualified primary conversion/i;

export type CampaignRunState =
  | 'CLIENT_BOOTSTRAP'
  | 'KEYWORD_RESEARCH'
  | 'KW_APPROVAL'
  | 'CAMPAIGN_CREATE'
  | 'AD_REVIEW'
  | 'ASSET_CREATE'
  | 'APPLY_RECS'
  | 'DONE'
  | 'FAILED'
  | 'REJECTED'
  | 'CANCELLED';

// ---------------------------------------------------------------------------
// Item J — RSA ad authoring (operator review/edit of AI-generated ad copy)
// ---------------------------------------------------------------------------

/** A single responsive-search-ad headline. `pin` locks it to a position
 *  (1|2|3) in the served ad, or `null` to let Google rotate it. Max 30 chars. */
export interface RsaHeadline {
  text: string;
  pin: 1 | 2 | 3 | null;
}

/** A single responsive-search-ad description. `pin` locks it to a position
 *  (1|2) in the served ad, or `null` to let Google rotate it. Max 90 chars. */
export interface RsaDescription {
  text: string;
  pin: 1 | 2 | null;
}

/**
 * One responsive search ad draft. Headlines 3–15 (≤30 chars each),
 * descriptions 2–4 (≤90 chars each), paths ≤15 chars. `from_fallback` marks a
 * draft that came from the deterministic fallback rather than the LLM.
 *
 * `name` is the operator-facing ad label (Google Ads "Ad name" — may be "").
 * `final_url` is the ad's landing page; when "" the backend defaults it to the
 * cluster's campaign landing page.
 */
export interface RsaDraft {
  headlines: RsaHeadline[];
  descriptions: RsaDescription[];
  path1: string;
  path2: string;
  name: string;
  final_url: string;
  from_fallback?: boolean;
}

export type CampaignBiddingStrategy =
  | 'MAXIMIZE_CONVERSIONS'
  | 'MAXIMIZE_CLICKS'
  | 'TARGET_CPA'
  | 'TARGET_ROAS';
export type CampaignGoal =
  | 'LEAD_GENERATION'
  | 'ROI_HOME_SERVICES'
  | 'BRAND_AWARENESS'
  | 'TRAFFIC_CLICKS'
  | 'MANUAL_TESTING';

export interface CampaignSetupRequest {
  tenant_id: string;
  business_name: string;

  /** Search (keyword) vs Performance Max (asset-group). Default SEARCH. */
  campaign_type?: 'SEARCH' | 'PERFORMANCE_MAX';

  /** Daily micros — caller can compute via `Math.round((monthly / 30.4) * 1_000_000)`. */
  daily_budget_micros: number;
  active_service_days_per_month?: number | null;
  campaign_goal?: CampaignGoal;
  bidding_strategy?: CampaignBiddingStrategy;
  target_cpa_micros?: number;
  target_roas?: number;
  smart_bidding_override_ack?: string;
  budget_allocation_strategy?: 'EVEN_BY_AD_GROUP' | 'WEIGHTED_BY_CPC_VOLUME';
  campaign_structure?: 'SINGLE_CAMPAIGN' | 'CAMPAIGN_PER_CLUSTER';
  target_search_network?: boolean;
  target_content_network?: boolean;

  /** RSAs per ad group to draft + create (2–3) for A/B testing; default 2. */
  rsa_variations?: number;

  target_address: string;
  gbp_location_resource_name?: string;
  primary_phone?: string;

  company_information: string;
  existing_target_keywords?: string[];
  existing_target_keyword_details?: KeywordResearchCampaignHandoffKeyword[];
  pages?: string[];
  language?: string;
  geo_target_constants?: string[];
  keyword_expansion_enabled?: boolean;

  services_offered?: string;
  value_proposition?: string;
  call_to_action?: string;
  promotions_specials?: string;
  cta_config?: Record<string, unknown> | null;

  status_on_launch?: 'PAUSED' | 'ENABLED';
  approval_channels?: string[];
  approval_expires_in_hours?: number;
  requested_by?: string;

  /** When true, the run pauses at AD_REVIEW so the operator can review/edit the
   *  AI-generated RSA copy before ads are created. Default false. */
  ad_review?: boolean;
}

export type CampaignPolicyCheckStatus = 'pass' | 'warning' | 'fail' | 'fixed';
export type CampaignPolicyCheckSeverity = 'info' | 'warning' | 'blocking';

export interface CampaignPolicyCheck {
  id: string;
  label: string;
  status: CampaignPolicyCheckStatus;
  severity: CampaignPolicyCheckSeverity;
  evidence: string;
}

export interface CampaignRunKeyword {
  keyword: string;
  cpc?: number | null;
  volume?: number | null;
}

export interface CampaignRunCluster {
  cluster_name?: string | null;
  landing_page?: string | null;
  keywords?: Array<string | CampaignRunKeyword>;
  [key: string]: unknown;
}

export interface CampaignReadbackCheck {
  id?: string;
  label?: string;
  status?: 'pass' | 'warning' | 'error' | 'fail' | string;
  evidence?: string;
}

export interface CampaignReadbackSummary {
  campaigns?: number;
  ad_groups?: number;
  keywords?: number;
  rsas?: number;
  negative_shared_sets?: number;
  conversion_actions?: number;
}

export interface CampaignReadbackCampaign {
  resource_name?: string;
  status?: 'pass' | 'warning' | 'error' | string;
  error?: string;
  counts?: CampaignReadbackSummary;
  checks?: CampaignReadbackCheck[];
}

export interface CampaignGoogleAdsReadback {
  status?: 'pass' | 'warning' | 'error' | string;
  checked_at?: string;
  customer_id?: string;
  campaigns?: CampaignReadbackCampaign[];
  summary?: CampaignReadbackSummary;
  checks?: CampaignReadbackCheck[];
}

export interface CampaignRunResponse {
  id: string;
  tenant_id: string;
  state: CampaignRunState;
  approval_gate_id: string | null;
  run_input: Record<string, unknown> & {
    google_ads_readback?: CampaignGoogleAdsReadback;
    /** Item J — AI-generated RSA drafts keyed by cluster name (2 per cluster),
     *  populated when the run reaches AD_REVIEW. */
    ad_drafts?: Record<string, RsaDraft[]>;
  };
  clusters: CampaignRunCluster[] | null;
  policy_checks?: CampaignPolicyCheck[];
  campaign_resource_name: string | null;
  ad_group_map: Record<string, unknown> | null;
  assets_created: unknown[] | null;
  recommendations_applied: string[] | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClusterListResponse {
  run_id: string;
  state: CampaignRunState;
  clusters: CampaignRunCluster[];
}

export interface CampaignRunsListResponse {
  tenant_id: string;
  runs: CampaignRunResponse[];
}

export interface CampaignAuditEvidenceRow {
  id: string;
  actor: string;
  operation: string;
  status: string;
  resource_type?: string | null;
  resource_id?: string | null;
  customer_id_ending?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  error_message?: string | null;
}

export interface CampaignAuditEvidenceResponse {
  run_id: string;
  tenant_id: string;
  state: CampaignRunState | string;
  campaign_resource_name?: string | null;
  customer_id_ending?: string | null;
  run_created_at?: string | null;
  run_updated_at?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  max_cpc_bid_ceiling_micros?: number | null;
  audit_total: number;
  by_status: Record<string, number>;
  by_operation_status: Record<string, number>;
  has_error_rows: boolean;
  rows: CampaignAuditEvidenceRow[];
}

export type LiveCampaignSource =
  | 'app_created'
  | 'google_ads_only'
  | 'removed_in_app'
  | 'missing_in_google_ads'
  | 'removed_in_google_ads';

export type LiveCampaignBudgetSyncStatus =
  | 'pass'
  | 'drift'
  | 'no_plan'
  | 'no_live_budget'
  | string;

export interface LiveCampaignBudgetSync {
  status: LiveCampaignBudgetSyncStatus;
  planned_daily_budget_micros?: number | null;
  live_daily_budget_micros?: number | null;
  delta_micros?: number | null;
  abs_delta_micros?: number | null;
  threshold_micros?: number | null;
  requires_review?: boolean;
  evidence?: string;
}

/**
 * LAST_30_DAYS performance for one campaign or ad group (#952 / #953).
 *
 * Money is micros throughout, matching the budget fields already in these
 * payloads. `cost_per_conversion_micros` is deliberately `null` — not `0` —
 * when there were no conversions, because "$0.00 per lead" reads as free
 * leads. Divide by 1,000,000 for dollars.
 */
export interface AdsPerformanceMetrics {
  impressions: number;
  clicks: number;
  /** Fraction, not a percentage: 0.05 means 5%. */
  ctr: number;
  cost_micros: number;
  conversions: number;
  /** Fraction, not a percentage. */
  conversion_rate: number;
  cost_per_conversion_micros: number | null;
}

export type AdsMetricsWindowStatus = 'ok' | 'error' | 'skipped';

/**
 * Echo of the window the metrics were read over, plus whether the read
 * actually succeeded. The backend never fails the whole listing on a metrics
 * error — it reports it here and leaves `metrics: null` on the rows — so the
 * UI has to distinguish "genuinely zero" from "could not read".
 */
export interface AdsMetricsWindow {
  /** e.g. "LAST_30_DAYS". */
  date_range: string;
  status: AdsMetricsWindowStatus | string;
  error?: string | null;
}

export interface LiveCampaignInventoryItem {
  resource_name: string;
  campaign_id?: string | null;
  customer_id: string;
  name: string;
  status?: string | null;
  advertising_channel_type?: string | null;
  bidding_strategy_type?: string | null;
  campaign_budget_resource_name?: string | null;
  daily_budget_micros?: number | null;
  planned_daily_budget_micros?: number | null;
  budget_sync?: LiveCampaignBudgetSync | null;
  source: LiveCampaignSource | string;
  source_label: string;
  matched_run_id?: string | null;
  matched_run_state?: CampaignRunState | string | null;
  matched_run_error?: string | null;
  removed_in_app_at?: string | null;
  synced_at: string;
  live_in_google_ads: boolean;
  manageable: boolean;
  /**
   * `null` when the campaign isn't live in Google Ads, or when the metrics
   * read failed / was skipped. A live campaign that simply didn't deliver in
   * the window gets zeros, not `null`.
   */
  metrics?: AdsPerformanceMetrics | null;
}

export interface LiveCampaignInventoryResponse {
  tenant_id: string;
  customer_id: string;
  synced_at: string;
  campaigns: LiveCampaignInventoryItem[];
  metrics_window?: AdsMetricsWindow | null;
  totals: {
    total: number;
    live: number;
    tracked: number;
    by_source: Record<string, number>;
    budget_sync?: Record<string, number>;
    /** Account rollup. Present only when `metrics_window.status === 'ok'`. */
    metrics?: AdsPerformanceMetrics;
  };
}

/** One ad group inside a campaign, with its LAST_30_DAYS performance (#953). */
export interface AdGroupPerformanceItem {
  resource_name: string;
  ad_group_id: string;
  customer_id: string;
  campaign_resource_name: string;
  campaign_name?: string | null;
  name?: string | null;
  status?: string | null;
  type?: string | null;
  cpc_bid_micros?: number | null;
  synced_at: string;
  /** `null` only when the metrics read failed; no delivery yields zeros. */
  metrics?: AdsPerformanceMetrics | null;
}

export interface AdGroupPerformanceResponse {
  tenant_id: string;
  customer_id: string;
  campaign_resource_name: string;
  campaign_name?: string | null;
  synced_at: string;
  metrics_window: AdsMetricsWindow;
  ad_groups: AdGroupPerformanceItem[];
  totals: {
    total: number;
    metrics?: AdsPerformanceMetrics;
  };
}

/** One ad (RSA) inside an ad group, with its LAST_30_DAYS performance — for
 *  A/B comparison of the variations within a group. */
export interface AdPerformanceItem {
  resource_name: string;
  ad_id: string;
  status?: string | null;
  ad_name?: string | null;
  /** First few RSA headlines, used as the ad's human label. */
  headlines: string[];
  final_url?: string | null;
  /** `null` only when the metrics read failed; no delivery yields zeros. */
  metrics?: AdsPerformanceMetrics | null;
}

export interface AdPerformanceResponse {
  /** ⚠ What the server would ACCEPT right now. The Pause button on this list
   *  mutates PATCH /live/ad-status, which needs a flag this READ does not — so
   *  a page that drew the button from a successful read offered something the
   *  server refuses. Read through canWrite(), never indexed directly. */
  capabilities?: import('./googleAdsManagementService').GaWriteCapabilities;

  tenant_id: string;
  customer_id: string;
  ad_group_resource_name: string;
  synced_at: string;
  metrics_window: AdsMetricsWindow;
  ads: AdPerformanceItem[];
  totals: {
    total: number;
    metrics?: AdsPerformanceMetrics;
  };
}

/** One keyword inside an ad group, with its LAST_30_DAYS performance —
 *  so operators/clients can see which keywords are working vs not. */
export interface KeywordPerformanceItem {
  resource_name: string;
  keyword_text?: string | null;
  match_type?: string | null;
  status?: string | null;
  /** `null` only when the metrics read failed; no delivery yields zeros. */
  metrics?: AdsPerformanceMetrics | null;
}

export interface KeywordPerformanceResponse {
  tenant_id: string;
  customer_id: string;
  ad_group_resource_name: string;
  synced_at: string;
  metrics_window: AdsMetricsWindow;
  keywords: KeywordPerformanceItem[];
  totals: {
    total: number;
    metrics?: AdsPerformanceMetrics;
  };
}

export interface LiveCampaignActionResponse {
  tenant_id?: string;
  customer_id?: string;
  campaign_resource_name: string;
  status?: string;
  daily_budget_micros?: number;
  campaign_budget_resource_name?: string;
  mutation_status?: 'success' | 'dry_run' | string;
}

export async function setupCampaign(
  request: CampaignSetupRequest,
): Promise<CampaignRunResponse> {
  const { data } = await apiClient.post<CampaignRunResponse>(
    '/google-ads/campaigns/setup',
    request,
    { timeout: CAMPAIGN_SETUP_TIMEOUT_MS },
  );
  return data;
}

export function describeCampaignRunError(error: string | null | undefined): string {
  const raw = error?.trim();
  if (!raw) return '';
  if (CONVERSION_TRACKING_GATE_PATTERN.test(raw)) {
    return CONVERSION_TRACKING_GATE_MESSAGE;
  }
  if (/Smart bidding conversion history/i.test(raw)) {
    return 'Maximize conversions is blocked because this account does not meet the 30-conversion smart-bidding history gate. Use Maximize clicks, wait for enough conversion history, or enter the exact smart-bidding override phrase after review.';
  }
  return raw;
}

export function describeCampaignSetupError(err: unknown): string {
  const response = (err as { response?: { data?: unknown; status?: number } })?.response;
  const responseData = response?.data;
  const detail =
    responseData && typeof responseData === 'object' && 'detail' in responseData
      ? (responseData as { detail?: unknown }).detail
      : undefined;

  if (
    detail
    && typeof detail === 'object'
    && (detail as { error?: unknown }).error === 'feature_disabled'
  ) {
    const flag = String((detail as { flag?: unknown }).flag ?? 'Google Ads feature flag');
    return `${flag} is disabled. Enable it in the backend environment before starting Campaign Creation.`;
  }

  const rawMessage =
    typeof detail === 'string'
      ? detail
      : (err as { message?: string })?.message ?? String(err);
  const responseText =
    typeof responseData === 'string'
      ? responseData
      : responseData
        ? JSON.stringify(responseData)
        : '';
  const diagnosticText = `${rawMessage} ${responseText}`;

  if (CONVERSION_TRACKING_GATE_PATTERN.test(diagnosticText)) {
    return CONVERSION_TRACKING_GATE_MESSAGE;
  }
  if (/Smart bidding conversion history/i.test(diagnosticText)) {
    return 'Maximize conversions is blocked because this account does not meet the 30-conversion smart-bidding history gate. Use Maximize clicks, wait for enough conversion history, or enter the exact smart-bidding override phrase after review.';
  }
  if (
    /queuepool|connection pool|max clients|too many connections|database connection/i.test(
      diagnosticText,
    )
  ) {
    return 'Backend database connection pool is exhausted. Restart the backend or wait for Supabase connections to free up, then retry Campaign Creation.';
  }
  if (
    (err as { code?: string })?.code === 'ECONNABORTED' ||
    /timeout of \d+ms exceeded|timed out/i.test(rawMessage)
  ) {
    return 'Campaign setup took longer than the browser wait window. Setup may still be running; wait a minute, check the latest campaign run state, then retry only if no run appears.';
  }
  if (/network error/i.test(rawMessage)) {
    return 'Could not reach Core. Check your connection, then retry Campaign Creation.';
  }
  if (/missing or invalid authorization header/i.test(rawMessage)) {
    return 'Authorization is missing or invalid. Refresh the app or sign in again before starting Campaign Creation.';
  }
  if (response?.status && response.status >= 500) {
    return 'Backend failed while starting Campaign Creation. Check the backend logs, then retry after the database/API issue is cleared.';
  }
  return rawMessage;
}

export async function getCampaignRun(
  runId: string,
): Promise<CampaignRunResponse> {
  const { data } = await apiClient.get<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${runId}`,
  );
  return data;
}

export async function refreshCampaignRunReadback(params: {
  runId: string;
  tenantId: string;
}): Promise<CampaignRunResponse> {
  const { data } = await apiClient.post<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${params.runId}/readback/refresh`,
    {},
    {
      params: {
        tenant_id: params.tenantId,
      },
    },
  );
  return data;
}

export async function getCampaignRunAuditEvidence(params: {
  runId: string;
  tenantId: string;
}): Promise<CampaignAuditEvidenceResponse> {
  const { data } = await apiClient.get<CampaignAuditEvidenceResponse>(
    `/google-ads/campaigns/runs/${params.runId}/audit-evidence`,
    {
      params: {
        tenant_id: params.tenantId,
      },
    },
  );
  return data;
}

export async function listCampaignRuns(params: {
  tenantId: string;
  state?: CampaignRunState;
  limit?: number;
}): Promise<CampaignRunsListResponse> {
  const { data } = await apiClient.get<CampaignRunsListResponse>(
    '/google-ads/campaigns/runs',
    {
      params: {
        tenant_id: params.tenantId,
        state: params.state,
        limit: params.limit ?? 50,
      },
    },
  );
  return data;
}

export async function listLiveCampaigns(params: {
  tenantId: string;
  limit?: number;
  /** Sent explicitly rather than leaning on the backend default, because the
   *  campaign list renders performance inline and would silently go blank if
   *  that default ever flipped. */
  includeMetrics?: boolean;
}): Promise<LiveCampaignInventoryResponse> {
  const { data } = await apiClient.get<LiveCampaignInventoryResponse>(
    '/google-ads/campaigns/live',
    {
      params: {
        tenant_id: params.tenantId,
        limit: params.limit ?? 200,
        include_metrics: params.includeMetrics ?? true,
      },
    },
  );
  return data;
}

/**
 * Ad-group rows + LAST_30_DAYS performance for ONE campaign (#953).
 *
 * Always campaign-scoped — the backend rejects a campaign that doesn't belong
 * to the tenant's resolved customer. Callers should fetch this lazily (on
 * expand); an account with 29 campaigns would otherwise mean 29 Google Ads
 * reads for data nobody opened.
 */
export async function listAdGroupPerformance(params: {
  tenantId: string;
  campaignResourceName: string;
  limit?: number;
}): Promise<AdGroupPerformanceResponse> {
  const { data } = await apiClient.get<AdGroupPerformanceResponse>(
    '/google-ads/campaigns/live/ad-groups',
    {
      params: {
        tenant_id: params.tenantId,
        campaign_resource_name: params.campaignResourceName,
        limit: params.limit ?? 200,
      },
    },
  );
  return data;
}

/** Per-ad (RSA) performance for one ad group — the A/B drill-down. */
export async function listAdPerformance(params: {
  tenantId: string;
  adGroupResourceName: string;
  limit?: number;
}): Promise<AdPerformanceResponse> {
  const { data } = await apiClient.get<AdPerformanceResponse>(
    '/google-ads/campaigns/live/ads',
    {
      params: {
        tenant_id: params.tenantId,
        ad_group_resource_name: params.adGroupResourceName,
        limit: params.limit ?? 200,
      },
    },
  );
  return data;
}

/** Per-keyword performance for one ad group — which keywords are working. */
export async function listKeywordPerformance(params: {
  tenantId: string;
  adGroupResourceName: string;
  limit?: number;
}): Promise<KeywordPerformanceResponse> {
  const { data } = await apiClient.get<KeywordPerformanceResponse>(
    '/google-ads/campaigns/live/keywords',
    {
      params: {
        tenant_id: params.tenantId,
        ad_group_resource_name: params.adGroupResourceName,
        limit: params.limit ?? 200,
      },
    },
  );
  return data;
}

export async function deleteCampaignRunCampaign(params: {
  runId: string;
  tenantId: string;
  deletedBy?: string;
}): Promise<CampaignRunResponse> {
  const { data } = await apiClient.delete<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${params.runId}/campaign`,
    {
      params: {
        tenant_id: params.tenantId,
        deleted_by: params.deletedBy ?? 'ui',
      },
    },
  );
  return data;
}

export async function deleteLiveCampaign(params: {
  tenantId: string;
  campaignResourceName: string;
  deletedBy?: string;
}): Promise<LiveCampaignActionResponse> {
  const { data } = await apiClient.delete<LiveCampaignActionResponse>(
    '/google-ads/campaigns/live',
    {
      params: {
        tenant_id: params.tenantId,
      },
      data: {
        campaign_resource_name: params.campaignResourceName,
        deleted_by: params.deletedBy ?? 'ui',
      },
    },
  );
  return data;
}

export async function updateCampaignRunCampaignStatus(params: {
  runId: string;
  tenantId: string;
  status: 'PAUSED' | 'ENABLED';
  updatedBy?: string;
}): Promise<CampaignRunResponse> {
  const { data } = await apiClient.patch<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${params.runId}/campaign/status`,
    {
      status: params.status,
      updated_by: params.updatedBy ?? 'ui',
    },
    {
      params: {
        tenant_id: params.tenantId,
      },
    },
  );
  return data;
}

export async function updateLiveCampaignStatus(params: {
  tenantId: string;
  campaignResourceName: string;
  status: 'PAUSED' | 'ENABLED';
  updatedBy?: string;
}): Promise<LiveCampaignActionResponse> {
  const { data } = await apiClient.patch<LiveCampaignActionResponse>(
    '/google-ads/campaigns/live/status',
    {
      campaign_resource_name: params.campaignResourceName,
      status: params.status,
      updated_by: params.updatedBy ?? 'ui',
    },
    {
      params: {
        tenant_id: params.tenantId,
      },
    },
  );
  return data;
}

export interface AdStatusUpdateResponse {
  tenant_id: string;
  customer_id: string;
  ad_resource_name: string;
  status: string;
  mutation_status: string;
}

/** Pause or enable a single ad (RSA) — direct operator write, audit-logged. */
export async function updateAdStatus(params: {
  tenantId: string;
  adResourceName: string;
  status: 'PAUSED' | 'ENABLED';
  updatedBy?: string;
}): Promise<AdStatusUpdateResponse> {
  const { data } = await apiClient.patch<AdStatusUpdateResponse>(
    '/google-ads/campaigns/live/ad-status',
    {
      ad_resource_name: params.adResourceName,
      status: params.status,
      updated_by: params.updatedBy ?? 'ui',
    },
    {
      params: {
        tenant_id: params.tenantId,
      },
    },
  );
  return data;
}

export async function updateCampaignRunCampaignBudget(params: {
  runId: string;
  tenantId: string;
  dailyBudgetMicros: number;
  updatedBy?: string;
}): Promise<CampaignRunResponse> {
  const { data } = await apiClient.patch<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${params.runId}/campaign/budget`,
    {
      daily_budget_micros: params.dailyBudgetMicros,
      updated_by: params.updatedBy ?? 'ui',
    },
    {
      params: {
        tenant_id: params.tenantId,
      },
    },
  );
  return data;
}

export async function updateLiveCampaignBudget(params: {
  tenantId: string;
  campaignResourceName: string;
  dailyBudgetMicros: number;
  updatedBy?: string;
}): Promise<LiveCampaignActionResponse> {
  const { data } = await apiClient.patch<LiveCampaignActionResponse>(
    '/google-ads/campaigns/live/budget',
    {
      campaign_resource_name: params.campaignResourceName,
      daily_budget_micros: params.dailyBudgetMicros,
      updated_by: params.updatedBy ?? 'ui',
    },
    {
      params: {
        tenant_id: params.tenantId,
      },
    },
  );
  return data;
}

export async function getCampaignRunClusters(
  runId: string,
): Promise<ClusterListResponse> {
  const { data } = await apiClient.get<ClusterListResponse>(
    `/google-ads/campaigns/runs/${runId}/clusters`,
  );
  return data;
}

export async function approveCampaignRun(
  runId: string,
  resolvedBy: string,
): Promise<CampaignRunResponse> {
  const { data } = await apiClient.post<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${runId}/approve`,
    { approved_by: resolvedBy, channel: 'ui' },
  );
  return data;
}

/**
 * Item J — submit operator-edited RSA copy for a run parked at AD_REVIEW.
 * `adCopy` mirrors the `ad_drafts` shape (`Record<clusterName, RsaDraft[]>`);
 * the backend persists it as `ad_copy` and advances the run to ASSET_CREATE.
 */
export async function submitCampaignRunAds(
  runId: string,
  tenantId: string,
  adCopy: Record<string, RsaDraft[]>,
  submittedBy: string,
): Promise<CampaignRunResponse> {
  const { data } = await apiClient.post<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${runId}/ads`,
    { ad_copy: adCopy, submitted_by: submittedBy },
    { params: { tenant_id: tenantId } },
  );
  return data;
}

/**
 * Item J — force a fresh AI regeneration of a single RSA draft for a run parked
 * at AD_REVIEW. The backend re-runs the LLM ad-copy generation for the given
 * cluster/variant, persists the result into `run_input.ad_drafts`, and returns
 * the new draft. A non-fallback result clears the fallback-block for that card.
 */
export async function regenerateCampaignRunAd(
  runId: string,
  tenantId: string,
  clusterName: string,
  variantIndex: number,
): Promise<{ cluster_name: string; variant_index: number; draft: RsaDraft }> {
  const { data } = await apiClient.post<{
    cluster_name: string;
    variant_index: number;
    draft: RsaDraft;
  }>(
    `/google-ads/campaigns/runs/${runId}/ads/regenerate`,
    { cluster_name: clusterName, variant_index: variantIndex },
    { params: { tenant_id: tenantId }, timeout: 120_000 },
  );
  return data;
}

export async function rejectCampaignRun(
  runId: string,
  resolvedBy: string,
): Promise<CampaignRunResponse> {
  const { data } = await apiClient.post<CampaignRunResponse>(
    `/google-ads/campaigns/runs/${runId}/reject`,
    { rejected_by: resolvedBy, channel: 'ui' },
  );
  return data;
}

/**
 * Response of the cancel endpoint: the serialized run plus a `cancel_requested`
 * flag. At KW_APPROVAL the returned `state` is already `'CANCELLED'`; at active
 * build states the flag is set and the run flips to `'CANCELLED'` at the next
 * checkpoint (the run panel's 5s refetch surfaces it). Cancelling a terminal
 * run → HTTP 409.
 */
export interface CampaignRunCancelResponse extends CampaignRunResponse {
  cancel_requested: boolean;
}

export async function cancelCampaignRun(
  runId: string,
  options: { cancelledBy?: string; reason?: string } = {},
): Promise<CampaignRunCancelResponse> {
  const { data } = await apiClient.post<CampaignRunCancelResponse>(
    `/google-ads/campaigns/runs/${runId}/cancel`,
    {
      cancelled_by: options.cancelledBy ?? 'ui',
      channel: 'ui',
      reason: options.reason,
    },
  );
  return data;
}

/** Helper: convert monthly $ budget to daily micros (1 USD = 1,000,000 micros). */
export function dailyBudgetMicros(monthlyUsd: number): number {
  return Math.round((monthlyUsd / 30.4) * 1_000_000);
}

// ---------------------------------------------------------------------------
// Standalone keyword-research preview (P2.9a)
// ---------------------------------------------------------------------------

export interface KeywordResearchPreviewRequest {
  tenant_id: string;
  company_information?: string;
  /** Preferred number of ad groups (1-20). ADVISORY, not a quota.
   *
   *  It guides how finely the surviving keywords are grouped — 40 keywords can
   *  honestly be 4 ad groups or 8. It cannot create ad groups the keyword set
   *  does not justify, because one ad group must be targetable by a single
   *  responsive search ad. Omit to let the pipeline decide. */
  target_cluster_count?: number;
  existing_target_keywords?: string[];
  seed_keyword_mode?: 'manual_plus_workspace' | 'manual_only';
  pages?: string[];
  location_names?: string[];
  /** Optional Google language constant (e.g., "languageConstants/1000").
   *  Undefined / empty → backend resolves from tenant locale. */
  language?: string;
  /** Optional Google geo target constants (e.g., ["geoTargetConstants/2036"]).
   *  Empty → backend resolves from tenant locale. */
  geo_target_constants?: string[];
}

export interface PreviewedKeyword {
  keyword: string;
  cpc: number;
  volume: number;
  // Phase A — Google Keyword Planner passthrough fields. Optional;
  // populated when the expander surfaced this keyword from Google.
  kp_avg_monthly_searches?: number | null;
  kp_competition?: 'LOW' | 'MEDIUM' | 'HIGH' | string | null;
  kp_competition_index?: number | null;
  kp_low_top_of_page_bid_micros?: number | null;
  kp_high_top_of_page_bid_micros?: number | null;
  kp_monthly_search_volumes?: Array<{
    year?: number | null;
    month?: string | null;
    monthly_searches?: number | null;
  }> | null;
  ad_impression_share?: number | null;
  organic_impression_share?: number | null;
  organic_average_position?: number | null;
  in_account?: boolean | null;
  in_plan?: boolean | null;
  selected_for_campaign?: boolean | null;
  source?: 'brainstorm' | 'keyword_planner' | null;
}

export interface PreviewedCluster {
  cluster_name: string;
  keywords: PreviewedKeyword[];
  landing_page: string | null;
  used?: boolean | null;
  used_in_campaign_run_id?: string | null;
  used_state?: string | null;
}

export interface NegativeKeywordCandidate {
  keyword: string;
  cpc: number | null;
  volume: number | null;
  reason: 'competitor_brand' | 'wrong_industry' | 'info_intent' | 'low_intent_modifier' | 'other' | string;
  reason_detail?: string | null;
}

export interface TargetKeywordSuggestion {
  keyword: string;
  source: string;
  service?: string | null;
  reason?: string | null;
}

export interface SeedKeywordAutomationLog {
  stage: string;
  status: 'info' | 'success' | 'warning' | string;
  message: string;
  detail?: string | null;
}

export interface KeywordResearchQualityCheck {
  id: string;
  label: string;
  status: 'pass' | 'warning' | 'fail' | string;
  severity: 'info' | 'warning' | 'blocking' | string;
  evidence: string;
}

export interface SeedKeywordAutomationPreviewRequest {
  tenant_id: string;
  company_information?: string;
  existing_target_keywords?: string[];
  pages?: string[];
  location_names?: string[];
  limit?: number;
}

/** What this tenant has already done with one keyword, across past runs. */
export interface PriorKeywordUse {
  keyword: string;
  /** The operator submitted it as a seed before. */
  submitted_as_seed: boolean;
  /** It came back in a previous run's ad groups. */
  returned_as_keyword: boolean;
  run_count: number;
  first_seen?: string | null;
  last_seen?: string | null;
  is_new: boolean;
  label: string;
}

export interface PriorKeywordUseSummary {
  total: number;
  new: number;
  tried_before_no_results: number;
  already_in_previous_runs: number;
}

export interface SeedKeywordAutomationPreviewResult {
  tenant_id: string;
  suggestions: TargetKeywordSuggestion[];
  logs: SeedKeywordAutomationLog[];
  company_information_preview?: string | null;
  client_research_context_available: boolean;
  /** Keyed by the suggestion's keyword. Absent entries are new. */
  prior_use?: Record<string, PriorKeywordUse>;
  prior_use_summary?: PriorKeywordUseSummary;
}

export interface SitePageSuggestion {
  url: string;
  title?: string | null;
  source: string;
  reason: string;
  score: number;
}

export interface SitePageSuggestionPreviewRequest {
  tenant_id: string;
  existing_pages?: string[];
  existing_target_keywords?: string[];
  limit?: number;
}

export interface SitePageSuggestionPreviewResult {
  tenant_id: string;
  suggestions: SitePageSuggestion[];
  logs: SeedKeywordAutomationLog[];
}

export interface KeywordResearchPreviewResult {
  /** Persisted google_ads_keyword_research_runs id for history/selection handoff. */
  run_id?: string | null;
  clusters: PreviewedCluster[];
  /** Phase A — auto-classified negative-keyword suggestions. */
  negative_keywords?: NegativeKeywordCandidate[];
  /** Seed keywords used for Keyword Planner, derived from workspace when omitted. */
  target_keyword_suggestions?: TargetKeywordSuggestion[];
  /** Operator-visible deterministic quality gates for export/approval. */
  quality_checks?: KeywordResearchQualityCheck[];
  /** Echo of language used (for "results for: en-AU" UI). */
  language?: string | null;
  /** Echo of geo target constants used. */
  geo_target_constants?: string[];
  /** Echo of the URL sent as Keyword Planner's site filter. */
  site_filter_url?: string | null;
}

export interface KeywordResearchSelectionRequest {
  tenant_id: string;
  selected_keywords: string[];
}

export interface KeywordResearchSelectionResult {
  run_id: string;
  tenant_id: string;
  selected_keyword_count: number;
  selected_keywords: string[];
  selection_saved_at?: string | null;
}

export interface KeywordResearchCampaignHandoffKeyword extends PreviewedKeyword {
  cluster_name: string;
  landing_page?: string | null;
  low_hanging_fruit?: {
    status: 'yes' | 'review' | 'no' | string;
    label: string;
    reason: string;
  } | null;
}

export interface KeywordResearchCampaignHandoff {
  source: 'keyword_research';
  tenant_id: string;
  run_id?: string | null;
  selected_keywords: string[];
  selected_keyword_details?: KeywordResearchCampaignHandoffKeyword[];
  pages: string[];
  saved_at: string;
  planner_language_label?: string | null;
  planner_geo_label?: string | null;
  site_filter_url?: string | null;
}

export function keywordResearchCampaignHandoffKey(tenantId: string): string {
  return `google_ads_keyword_research_campaign_handoff:${tenantId}`;
}

export async function previewSeedKeywordAutomation(
  request: SeedKeywordAutomationPreviewRequest,
): Promise<SeedKeywordAutomationPreviewResult> {
  const { data } = await apiClient.post<SeedKeywordAutomationPreviewResult>(
    '/google-ads/keyword-research/seed-suggestions',
    request,
    {
      timeout: 45_000,
    },
  );
  return data;
}

export async function previewSitePageSuggestions(
  request: SitePageSuggestionPreviewRequest,
): Promise<SitePageSuggestionPreviewResult> {
  const { data } = await apiClient.post<SitePageSuggestionPreviewResult>(
    '/google-ads/keyword-research/site-page-suggestions',
    request,
    {
      timeout: 30_000,
    },
  );
  return data;
}

export async function previewAdCampaignKeywords(
  request: KeywordResearchPreviewRequest,
): Promise<KeywordResearchPreviewResult> {
  const { data } = await apiClient.post<KeywordResearchPreviewResult>(
    '/google-ads/keyword-research',
    request,
    {
      // Full pipeline does brainstorm + Keyword Planner expansion +
      // Ahrefs enrichment (rate-limited, ~1.5s/keyword) + LLM filter +
      // clustering + landing-page assignment. Easily 60-90s when all
      // stages fire — bump well past the 30s axios default.
      timeout: 180_000,
    },
  );
  return data;
}

export async function saveKeywordResearchSelection(
  runId: string,
  request: KeywordResearchSelectionRequest,
): Promise<KeywordResearchSelectionResult> {
  const { data } = await apiClient.post<KeywordResearchSelectionResult>(
    `/google-ads/keyword-research/history/${runId}/selection`,
    request,
  );
  return data;
}


// ---------------------------------------------------------------------------
// Phase C.2 — Add selected negatives to account-wide shared set
// ---------------------------------------------------------------------------

export interface AddNegativeKeywordsRequest {
  tenant_id: string;
  keywords: string[];
  match_type?: 'EXACT' | 'PHRASE';
  phrase_match_opt_in?: boolean;
  confirmation_text?: string;
  dry_run?: boolean;
}

export interface AddNegativeKeywordsResult {
  added: number;
  skipped: number;
  attached_campaigns?: number;
  errors_recorded?: number;
  match_type?: 'EXACT' | 'PHRASE';
  dry_run?: boolean;
  confirmation_required?: string;
  close_variant_warning?: string;
}

export interface AppliedNegativeKeyword {
  keyword: string;
  match_type: string;
  resource_name?: string;
}

export interface AppliedNegativeKeywordsResult {
  tenant_id: string;
  shared_set_name: string;
  shared_set_resource_name?: string | null;
  count: number;
  keywords: AppliedNegativeKeyword[];
}

export async function addNegativeKeywords(
  request: AddNegativeKeywordsRequest,
): Promise<AddNegativeKeywordsResult> {
  const { data } = await apiClient.post<AddNegativeKeywordsResult>(
    '/google-ads/keyword-research/negatives',
    request,
  );
  return data;
}


// ---------------------------------------------------------------------------
// Phase D.2 — Brand-defense SERP probe
// ---------------------------------------------------------------------------

export async function listAppliedNegativeKeywords(
  tenantId: string,
): Promise<AppliedNegativeKeywordsResult> {
  const { data } = await apiClient.get<AppliedNegativeKeywordsResult>(
    '/google-ads/keyword-research/negatives/applied',
    { params: { tenant_id: tenantId } },
  );
  return data;
}

export interface CompetitorAdSeen {
  title: string;
  link: string;
  domain: string;
  snippet?: string | null;
}

export interface BrandDefenseProbeResult {
  enabled: boolean;
  brand_name?: string | null;
  competitors_bidding: boolean;
  competitor_count: number;
  competitors_seen: CompetitorAdSeen[];
  skipped_reason?: string | null;
}

export async function fetchBrandDefenseProbe(
  tenantId: string,
): Promise<BrandDefenseProbeResult> {
  const { data } = await apiClient.get<BrandDefenseProbeResult>(
    '/google-ads/keyword-research/brand-defense',
    { params: { tenant_id: tenantId }, timeout: 15_000 },
  );
  return data;
}

export interface SponsoredAdSeen {
  title: string;
  /** Advertiser / business name (e.g. "Lime Digital Asia") — populated by the
   * bookmarklet capture; absent on provider results. */
  advertiser?: string | null;
  url?: string | null;
  display_url?: string | null;
  snippet?: string | null;
  sitelinks: string[];
  position?: number | null;
  domain?: string | null;
}

export interface SponsoredAdProbeResult {
  enabled: boolean;
  keyword?: string | null;
  gl?: string | null;
  hl?: string | null;
  sponsored_results: SponsoredAdSeen[];
  skipped_reason?: string | null;
}

/** Ads are geo-targeted: `market` is the 2-letter country code sent as
 * Serper's `gl` param — `ph` surfaces Philippines-only sponsored blocks
 * that never appear in the default `us` SERP. */
export async function fetchSponsoredAdProbe(
  query: string,
  market: string = 'us',
): Promise<SponsoredAdProbeResult> {
  const { data } = await apiClient.get<SponsoredAdProbeResult>(
    '/google-ads/keyword-research/sponsored-ads',
    { params: { q: query, gl: market }, timeout: 15_000 },
  );
  return data;
}

export interface BookmarkletCaptureResult {
  enabled: boolean;
  captured: boolean;
  keyword?: string | null;
  gl?: string | null;
  captured_at?: number | null;
  sponsored_results: SponsoredAdSeen[];
  skipped_reason?: string | null;
}

/** Poll for the latest client-side ('bookmarklet') sponsored-ad capture
 * for a tenant. The bookmarklet POSTs scrapes directly to the ingest
 * endpoint from google.com; the app reads them back here. */
export interface SponsoredAdRankFactors {
  keyword_relevance: string;
  ad_copy_tactics: string[];
  extensions: string;
  landing_page_experience: string;
}

export interface SponsoredAdPerAdAnalysis {
  advertiser?: string | null;
  domain?: string | null;
  why_it_ranks: string;
  ad_rank_factors: SponsoredAdRankFactors;
}

export interface SponsoredAdRecommendation {
  area: string;
  action: string;
  priority: string;
  rationale?: string | null;
}

export interface SponsoredAdAnalysis {
  query?: string | null;
  location?: string | null;
  ads_analyzed: number;
  patterns: string[];
  per_ad: SponsoredAdPerAdAnalysis[];
  recommendations: SponsoredAdRecommendation[];
  skipped_reason?: string | null;
}

/** Reverse-engineer the captured sponsored block: why each advertiser wins the
 * paid placement + recommendations to apply to the operator's pages/campaigns. */
export async function analyzeSponsoredAds(params: {
  tenantId: string;
  query: string;
  gl?: string | null;
  ads: SponsoredAdSeen[];
}): Promise<SponsoredAdAnalysis> {
  const { data } = await apiClient.post<SponsoredAdAnalysis>(
    '/google-ads/keyword-research/sponsored-ads/analyze',
    {
      tenant_id: params.tenantId,
      query: params.query,
      gl: params.gl ?? null,
      ads: params.ads,
    },
    { timeout: 120_000 },
  );
  return data;
}

export async function fetchBookmarkletCapture(
  tenantId: string,
): Promise<BookmarkletCaptureResult> {
  const { data } = await apiClient.get<BookmarkletCaptureResult>(
    '/google-ads/keyword-research/sponsored-ads/bookmarklet-capture',
    { params: { tenant_id: tenantId }, timeout: 15_000 },
  );
  return data;
}

/** Persist a bookmarklet capture (received via the app's URL fragment) to the
 * backend so it survives reloads. Best-effort — display already works from the
 * fragment, so callers should ignore failures. */
export async function ingestBookmarkletCapture(
  tenantId: string,
  capture: {
    keyword?: string | null;
    gl?: string | null;
    sponsored_results: Array<Record<string, unknown>>;
  },
): Promise<void> {
  await apiClient.post(
    '/google-ads/keyword-research/sponsored-ads/bookmarklet-ingest',
    {
      token: tenantId,
      keyword: capture.keyword ?? '',
      gl: capture.gl ?? '',
      sponsored_results: capture.sponsored_results,
    },
    { timeout: 15_000 },
  );
}


// ---------------------------------------------------------------------------
// Phase E.1 — Geo target search (multi-select location picker)
// ---------------------------------------------------------------------------

export interface GeoTargetSuggestion {
  id: string;
  resource_name: string;
  name: string;
  canonical_name: string;
  /** "Country" | "Region" | "Metro" (DMA) | "City" | "Postal Code" | etc. */
  target_type: string;
  country_code?: string | null;
  parent_resource_name?: string | null;
  status?: string | null;
  /** Google's audience-reach estimate when available. */
  reach?: number | null;
  query?: string | null;
  source?: string | null;
  reason?: string | null;
}

export interface GeoTargetSearchResult {
  query: string;
  results: GeoTargetSuggestion[];
}

export interface GeoTargetSuggestionPreviewResult {
  tenant_id: string;
  suggestions: GeoTargetSuggestion[];
  logs: SeedKeywordAutomationLog[];
  queries: string[];
}

/**
 * Autocomplete-style search over Google Ads geo targets.
 *
 * Backend caches results 24h in Redis. Caller should debounce keystrokes
 * (~300ms) to avoid hammering the endpoint.
 */
// ---------------------------------------------------------------------------
// Auto-fill company information — synthesize from brand bible +
// indexed pages + cached home-page scrape via an LLM pass.
// ---------------------------------------------------------------------------

export interface SynthesizeCompanyInfoResult {
  tenant_id: string;
  company_information: string;
  sources_used: string[];
  /** True when the brief was persisted to brand_bible.keyword_research_brief. */
  saved_to_brand_bible?: boolean;
  /** ISO timestamp of when the brief was last saved. */
  generated_at?: string | null;
}

export async function synthesizeCompanyInfo(
  tenantId: string,
): Promise<SynthesizeCompanyInfoResult> {
  const { data } = await apiClient.post<SynthesizeCompanyInfoResult>(
    '/google-ads/keyword-research/synthesize-company-info',
    { tenant_id: tenantId },
    { timeout: 60_000 },
  );
  return data;
}


export interface SaveCompanyInfoBriefResult {
  tenant_id: string;
  saved: boolean;
  generated_at?: string | null;
}

/** Persist an operator-edited company info brief to
 *  brand_bible.keyword_research_brief. */
export async function saveCompanyInfoBrief(params: {
  tenantId: string;
  companyInformation: string;
}): Promise<SaveCompanyInfoBriefResult> {
  const { data } = await apiClient.post<SaveCompanyInfoBriefResult>(
    '/google-ads/keyword-research/save-company-info-brief',
    {
      tenant_id: params.tenantId,
      company_information: params.companyInformation,
    },
  );
  return data;
}


export async function searchGeoTargets(params: {
  tenantId: string;
  query: string;
  countryCode?: string;
  locale?: string;
  pageSize?: number;
  signal?: AbortSignal;
}): Promise<GeoTargetSearchResult> {
  const { data } = await apiClient.get<GeoTargetSearchResult>(
    '/google-ads/keyword-research/geo-targets/search',
    {
      params: {
        tenant_id: params.tenantId,
        q: params.query,
        country_code: params.countryCode,
        locale: params.locale ?? 'en',
        page_size: params.pageSize ?? 20,
      },
      signal: params.signal,
      timeout: 30_000,
    },
  );
  return data;
}

/** CORE-K-08.b: one Brand Bible name resolved to a Google Ads geo target. */
export interface GeoTargetResolvedEntry {
  name: string;
  target: GeoTargetSuggestion;
}

export interface GeoTargetResolveResult {
  tenant_id: string;
  resolved: GeoTargetResolvedEntry[];
  /** Input names the catalog could not match, verbatim. */
  unmatched: string[];
}

/**
 * CORE-K-08.b: resolve plain workspace location names (Brand Bible
 * `service_locations` / `locations`) to Google Ads geo targets so the
 * operator can promote them into the picker's selected list.
 */
export async function resolveGeoTargets(params: {
  tenantId: string;
  names: string[];
  countryCode?: string;
  locale?: string;
  signal?: AbortSignal;
}): Promise<GeoTargetResolveResult> {
  const { data } = await apiClient.post<GeoTargetResolveResult>(
    '/google-ads/keyword-research/geo-targets/resolve',
    {
      tenant_id: params.tenantId,
      names: params.names,
      country_code: params.countryCode,
      locale: params.locale ?? 'en',
    },
    {
      signal: params.signal,
      timeout: 30_000,
    },
  );
  return data;
}

export async function previewGeoTargetSuggestions(params: {
  tenantId: string;
  locale?: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<GeoTargetSuggestionPreviewResult> {
  const { data } = await apiClient.get<GeoTargetSuggestionPreviewResult>(
    '/google-ads/keyword-research/geo-targets/suggestions',
    {
      params: {
        tenant_id: params.tenantId,
        locale: params.locale ?? 'en',
        limit: params.limit ?? 3,
      },
      signal: params.signal,
      timeout: 12_000,
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Suggestions for service-page form (Phase C/D)
// ---------------------------------------------------------------------------

export interface ClusterSuggestion {
  cluster_name: string;
  keywords: PreviewedKeyword[];
  landing_page: string | null;
  match_reason: 'url' | 'service_name';
}

export interface KeywordSuggestionsResponse {
  tenant_id: string;
  suggestions: ClusterSuggestion[];
}

export async function fetchKeywordSuggestions(params: {
  tenantId: string;
  serviceName?: string;
  landingPageUrl?: string;
}): Promise<KeywordSuggestionsResponse> {
  const { data } = await apiClient.get<KeywordSuggestionsResponse>(
    '/google-ads/keyword-research/suggestions',
    {
      params: {
        tenant_id: params.tenantId,
        service_name: params.serviceName,
        landing_page_url: params.landingPageUrl,
      },
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Tenant published pages (Phase F2) — used by the cluster preview to flag
// clusters whose landing_page isn't a published page on the tenant's site.
// ---------------------------------------------------------------------------

export interface TenantPagesResponse {
  tenant_id: string;
  pages: string[];
}

export async function fetchTenantPages(
  tenantId: string,
): Promise<TenantPagesResponse> {
  const { data } = await apiClient.get<TenantPagesResponse>(
    '/google-ads/keyword-research/tenant-pages',
    { params: { tenant_id: tenantId } },
  );
  return data;
}

/**
 * Mirror of backend `normalize_url` (keyword_research_sourcing.py).
 *
 * Lowercases scheme + host, preserves path case, strips ALL trailing
 * slashes (so "https://acme.com" and "https://acme.com/" canonicalize
 * identically), strips query string + fragment.
 */
export function normalizeUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    const scheme = parsed.protocol.replace(/:$/, '').toLowerCase();
    const host = parsed.host.toLowerCase();
    const path = parsed.pathname.replace(/\/+$/, ''); // strip ALL trailing slashes
    return `${scheme}://${host}${path}`;
  } catch {
    return trimmed.toLowerCase().replace(/\/+$/, '');
  }
}

/** Whether a workspace can launch Performance Max (Google's image minimums). */
export interface PmaxImageReadiness {
  tenant_id: string;
  ready: boolean;
  covered: string[];
  missing: string[];
  uploaded_count: number;
  message: string;
  /** False when GOOGLE_ADS_PMAX_ENABLED is off — no image count can fix that. */
  feature_enabled?: boolean;
}

/**
 * Can this workspace launch PMax?
 *
 * Google rejects the asset-group mutate AFTER the campaign is created, so the
 * builder asks up front rather than letting the operator find out by losing a
 * run to it.
 */
export async function getPmaxImageReadiness(
  tenantId: string,
): Promise<PmaxImageReadiness> {
  const { data } = await apiClient.get<PmaxImageReadiness>(
    '/google-ads/image-assets/pmax-readiness',
    { params: { tenant_id: tenantId } },
  );
  return data;
}
